import { useEffect, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '@clerk/react';
import {
  ASK_ADMIN,
  ASK_INVITE,
  resolveCanAskColleaguesToShare,
  usePremiumOffer,
} from './premiumOffer';
import { useSignInModal } from './SignInModalContext';
import { signInSourceForPath } from '../utils/signInCopy';

const DEEP_LINK_LOCATION = 'deep_link';

// ?modal= values, matching AD_MODALS in src/ads/useAdModal.js, mapped to the ask each opens.
const DEEP_LINK_ASKS = {
  shareWithAdmin: ASK_ADMIN,
  shareForPremium: ASK_INVITE,
};

/**
 * `?modal=shareWithAdmin` / `?modal=shareForPremium` deep-links straight into the expiration
 * ads' modals, so a marketing link into the homepage can land with the ask already open.
 *
 * Lives outside PremiumOfferProvider's own body on purpose: that provider is rendered OUTSIDE
 * SignInModalProvider in Base's tree (it renders SignInModalProvider as its `children`), so
 * useSignInModal() isn't reachable there. This component is a descendant of both, the same way
 * ForcedSignupPrompt is, and must be rendered inside SignInModalProvider (see base.jsx).
 *
 * Eligibility reuses the same signals the ad CTAs target (useAdContext's canShareWithAdmin /
 * canAskColleaguesToShare) rather than a new rule, so the link only opens where the ask
 * actually works for this teacher.
 */
export const PremiumOfferDeepLink = () => {
  const [query, setQuery] = useSearchParams();
  const { isSignedIn, isLoaded } = useAuth();
  const location = useLocation();
  const showSignInModal = useSignInModal();
  const { isOffered, ask, isSettledForUser, canShareWithAdmin, openPremiumOfferModal } =
    usePremiumOffer();
  // Guards the sign-in prompt so it fires once per visit, not on every re-render while the
  // teacher sits signed out with the param still in the URL.
  const signInPromptedRef = useRef(false);

  useEffect(() => {
    const modalKey = query.get('modal');
    if (!modalKey) return;

    const stripModalParam = () =>
      setQuery(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('modal');
          return next;
        },
        { replace: true }
      );

    const requestedAsk = DEEP_LINK_ASKS[modalKey];
    if (!requestedAsk) {
      stripModalParam(); // not one of ours
      return;
    }

    if (!isLoaded) return; // wait for Clerk before deciding signed-in vs signed-out

    if (!isSignedIn) {
      if (!signInPromptedRef.current) {
        signInPromptedRef.current = true;
        showSignInModal({ source: signInSourceForPath(location.pathname) });
      }
      return; // keep the param so the ask reopens once signed in
    }

    if (!isSettledForUser) return; // wait for THIS user's offer lookup, not the pre-load default

    const eligible =
      requestedAsk === ASK_ADMIN
        ? canShareWithAdmin
        : resolveCanAskColleaguesToShare(isOffered, ask);
    if (eligible) openPremiumOfferModal(requestedAsk, DEEP_LINK_LOCATION);
    stripModalParam();
  }, [
    query,
    isLoaded,
    isSignedIn,
    isSettledForUser,
    isOffered,
    ask,
    canShareWithAdmin,
    openPremiumOfferModal,
    showSignInModal,
    location.pathname,
    setQuery,
  ]);

  return null;
};
